import { useEffect } from 'react';
import { useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useApiAuth } from '@/hooks/use-api-auth';
import { useAuthQuery } from './use-auth-query';
import type { PaginatedResponse } from '@property/shared';

export interface BatchFetcherOpts {
  page: number;
  limit: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  [key: string]: unknown;
}

export interface BatchedPaginationOpts {
  viewPage: number;
  viewPageSize: number;
  /** How many records to fetch per API request. Must be a multiple of viewPageSize. Defaults to viewPageSize × 5. */
  apiBatchSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  /** Additional filter params forwarded verbatim to the fetcher. */
  filters?: Record<string, unknown>;
  staleTime?: number;
  /** When false the query (and prefetch) are skipped entirely. Defaults to true. */
  enabled?: boolean;
}

export interface BatchedPaginationResult<T> {
  data: T[];
  total: number;
  isLoading: boolean;
  isFetching: boolean;
  isStale: boolean;
  isError: boolean;
}

/**
 * Decouples view page size from API batch size.
 *
 * Example with viewPageSize=10, apiBatchSize=50:
 *   - View pages 1–5  → one API fetch (batch 1, records 1–50)
 *   - View pages 6–10 → one API fetch (batch 2, records 51–100)
 *   - Next batch is prefetched when the user reaches the last view-page of the current batch.
 *
 * The query key hierarchy is [...queryKeyBase, 'batch', batchParams] so that
 * qc.invalidateQueries({ queryKey: queryKeyBase }) cascades to all cached batches.
 */
export function useBatchedPaginatedQuery<T>(
  queryKeyBase: readonly unknown[],
  fetcher: (opts: BatchFetcherOpts) => Promise<PaginatedResponse<T>>,
  opts: BatchedPaginationOpts,
): BatchedPaginationResult<T> {
  const queryClient = useQueryClient();
  const { isAuthReady } = useApiAuth();

  const {
    viewPage,
    viewPageSize,
    sortBy,
    sortOrder,
    filters,
    staleTime = 1000 * 60 * 5,
    enabled = true,
  } = opts;
  const apiBatchSize = opts.apiBatchSize ?? viewPageSize * 5;
  const viewsPerBatch = Math.floor(apiBatchSize / viewPageSize);
  // Which API batch (0-indexed) contains the current view page
  const apiBatch = Math.floor((viewPage - 1) / viewsPerBatch);
  const apiPage = apiBatch + 1;
  // Offset within the batch for slicing
  const sliceStart = ((viewPage - 1) % viewsPerBatch) * viewPageSize;

  const batchKey = [
    ...queryKeyBase,
    'batch',
    { apiPage, apiBatchSize, sortBy, sortOrder, ...filters },
  ] as const;

  const result = useAuthQuery<PaginatedResponse<T>>({
    queryKey: batchKey,
    queryFn: () => fetcher({ page: apiPage, limit: apiBatchSize, sortBy, sortOrder, ...filters }),
    placeholderData: keepPreviousData,
    staleTime,
    enabled,
    // Override the global refetchOnMount:false so invalidated data actually refetches when an
    // observer mounts (e.g. DashboardOverview after property creation invalidates the cache).
    refetchOnMount: true,
  });

  // Prefetch the next batch when the user is on the last view-page of the current batch
  const isLastViewInBatch = (viewPage - 1) % viewsPerBatch === viewsPerBatch - 1;
  useEffect(() => {
    if (!isAuthReady || !isLastViewInBatch || !enabled) return;
    const nextApiPage = apiPage + 1;
    const nextKey = [
      ...queryKeyBase,
      'batch',
      { apiPage: nextApiPage, apiBatchSize, sortBy, sortOrder, ...filters },
    ];
    queryClient.prefetchQuery({
      queryKey: nextKey,
      queryFn: () =>
        fetcher({ page: nextApiPage, limit: apiBatchSize, sortBy, sortOrder, ...filters }),
      staleTime,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthReady, isLastViewInBatch, apiPage]);

  return {
    data: result.data?.data?.slice(sliceStart, sliceStart + viewPageSize) ?? [],
    total: result.data?.pagination?.total ?? 0,
    // isPending covers both "disabled (auth not ready)" and "actively loading" — both mean
    // no data yet. TanStack Query v5's isLoading = isFetching && isPending, which is false
    // for a disabled query even when it has no data, so it cannot be used here.
    isLoading: result.isPending,
    isFetching: result.isFetching,
    isStale: result.isStale,
    isError: result.isError,
  };
}
